import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Collapse,
  Alert,
  AlertTitle,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { AppDispatch, AppState, AppStore } from '../../../store';
import { EActionType, EAction } from '../../../data/Constants';
import { ErrorMessage } from '../../ErrorMessage';
import SearchIcon from '@mui/icons-material/Search';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import {
  getPriceListMap,
  getPriceListMapDetails,
  updatePriceListMap,
} from '../../../store/states/PriceSlice';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { resetAllErrorInfo } from '../../../services/DataHelperFunctions';

import type {
  ICountry,
  IManipulationProps,
  IPriceListMapData,
  IAPIResponse,
} from '../../../../types';

interface IHandleSearchProps {
  countryList: ICountry[];
  searchVal: string;
  setFilteredCountryList: (value: React.SetStateAction<ICountry[]>) => void;
}

interface ISearchBarProps {
  countryList: ICountry[];
  searchValue: string;
  filteredCountryList: ICountry[];
  setSearchValue: (value: React.SetStateAction<string>) => void;
  setFilteredCountryList: (value: React.SetStateAction<ICountry[]>) => void;
}

function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const priceListMap = AppStore.getState().dataMaintainance.priceListMap;
  if (res?.data) {
    dispatch(
      getPriceListMap({
        page: priceListMap.currentPage,
        limit: priceListMap.recordsPerPage,
        searchCode: priceListMap.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

//call api to Update price list map
function callPriceListMapUpdateApi(
  formData: IPriceListMapData,
  t: (message: string) => string,
  dispatch: AppDispatch
) {
  dispatch(updatePriceListMap({ request: { priceMaps: [formData] } })).then((res) => {
    handleApiResponse(
      res.payload as IAPIResponse,
      t('messages.fail.update'),
      t('messages.success.update'),
      dispatch
    );
  });
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialPriceMapData: (value: React.SetStateAction<IPriceListMapData>) => void,
  setFormData: (value: React.SetStateAction<IPriceListMapData>) => void,
  setCountryList: (value: React.SetStateAction<ICountry[]>) => void,
  setFilteredCountryList: (value: React.SetStateAction<ICountry[]>) => void,
  dispatch: AppDispatch
): Promise<void> => {
  const countryList = (AppStore.getState().dataMaintainance.countries as ICountry[]).filter(
    (c) => c.isActive
  );
  const priceListMap = await dispatch(getPriceListMapDetails({ searchCode: props.id })).unwrap();

  if (!priceListMap || 'error' in priceListMap || !priceListMap?.data?.priceMaps[0]) {
    return;
  }

  const { map, country } = priceListMap.data.priceMaps[0];
  const formData = { map, country };
  setCountryList(countryList);
  setFilteredCountryList(countryList);
  setInitialPriceMapData(formData);
  setFormData(formData);
};

const handleChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  setFormData: (value: React.SetStateAction<IPriceListMapData>) => void
) => {
  const name = event.target.name;
  const value = event.target.value;
  setFormData((values) => ({ ...values, [name]: value }));
};

const handleSearch = (props: IHandleSearchProps) => {
  const { countryList, searchVal, setFilteredCountryList } = props;
  const filteredData = countryList.filter(
    (country) =>
      country.name.toLowerCase().includes(searchVal.toLowerCase()) ||
      country.code.toLowerCase().includes(searchVal.toLowerCase())
  );
  setFilteredCountryList(filteredData);
};

const SearchBar = (props: ISearchBarProps) => {
  const { countryList, searchValue, filteredCountryList, setSearchValue, setFilteredCountryList } =
    props;
  return (
    <div className="priceListMapSearchBar">
      <Autocomplete
        id="country-search-box"
        clearOnBlur
        freeSolo
        value={searchValue}
        options={[]}
        className="search-bar"
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={(e) =>
              handleSearch({
                countryList,
                searchVal: e?.target?.value?.trim(),
                setFilteredCountryList,
              })
            }
            className="search-inner-text"
            placeholder={'Search'}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        onChange={(_, newVal) => {
          const searchVal = newVal?.trim() || '';
          setSearchValue(searchVal);
          handleSearch({ countryList, searchVal, setFilteredCountryList });
        }}
      />
      {searchValue ? (
        <div className="records">{'Records: ' + filteredCountryList.length}</div>
      ) : null}
    </div>
  );
};

export const PriceListMapManipulation = (props: IManipulationProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ country: '', map: '' });
  const [initialPriceMapData, setInitialPriceMapData] = useState({ country: '', map: '' });
  const [searchValue, setSearchValue] = useState('');
  const [countryList, setCountryList] = useState<ICountry[]>([]);
  const [filteredCountryList, setFilteredCountryList] = useState<ICountry[]>([]);
  const [disableButton, setDisableButton] = useState(true);
  const [displayAlertMessage, setDisplayAlertMessage] = useState(false);
  const manipulationType = props.type; //To get the type of manipulation being performed

  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: AppState) => state.error);

  useEffect(() => {
    const detailsUpdated = JSON.stringify(initialPriceMapData) !== JSON.stringify(formData);
    setDisableButton(!detailsUpdated);
  });

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(
        props,
        setInitialPriceMapData,
        setFormData,
        setCountryList,
        setFilteredCountryList,
        dispatch
      );
    }
  }, []);

  useEffect(() => {
    if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
      setDisplayAlertMessage(true);
    }
  }, [error]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };

  const handleSubmit = (event: React.MouseEvent) => {
    event.preventDefault();
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callPriceListMapUpdateApi(formData, t, dispatch);
  };

  const handleCancel = () => {
    resetAllErrorInfo();
    setDisplayAlertMessage(false);
  };

  return (
    <Box className="manipulationBox pt-1">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <TextField
          name="country"
          label={t('labels.country')}
          disabled
          variant="outlined"
          size="small"
          fullWidth
          value={formData.country}
          slotProps={{
            htmlInput: { 'data-testid': 'pricelist-country-input' },
          }}
        />
        <br />
        <br />
        <TextField
          name="map"
          label={t('labels.map')}
          disabled
          variant="outlined"
          size="small"
          fullWidth
          value={initialPriceMapData.map}
          slotProps={{
            htmlInput: { 'data-testid': 'pricelist-map-input' },
          }}
        />
        <br />
        <br />
        <fieldset className="inputFieldset w-100">
          <legend className="inputLegend" id="map">
            {t('labels.newMap')}
          </legend>
          {SearchBar({
            countryList,
            searchValue,
            filteredCountryList,
            setSearchValue,
            setFilteredCountryList,
          })}
          {countryList.length < 1 ? (
            <p>{t('messages.noCountriesFound')}</p>
          ) : (
            <RadioGroup
              aria-labelledby="map"
              name="map"
              value={formData.map}
              onChange={(e) => {
                handleChange(e, setFormData);
              }}
            >
              <Grid container>
                {filteredCountryList.map((value: ICountry) => {
                  return (
                    <Grid key={value.code} className="pt-0" size={4}>
                      <FormControlLabel
                        value={value.code}
                        label={value.code + ' (' + value.name + ')'}
                        control={<Radio data-testid="pricelist-country-radio" />}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          )}
        </fieldset>
        <Box className="formActionButtons">
          <Button
            data-testid="pricelist-submit-button"
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            className="text-capitalize"
            disabled={disableButton}
          >
            {t('button.save')}
          </Button>
          <Button
            data-testid="pricelist-cancel-button"
            size="medium"
            onClick={handleCancel}
            className="text-capitalize"
          >
            {t('button.cancel')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
